import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { toPlain } from "@/lib/fieldCodec";
import { CollectionList } from "@/components/explorer/CollectionList";
import { DocTable } from "@/components/explorer/DocTable";
import { DocumentPanel } from "@/components/explorer/DocumentPanel";
import { canSuperEdit } from "@/components/explorer/DocumentEditor";
import { NewDocDialog } from "@/components/explorer/NewDocDialog";
import { BulkEditDialog } from "@/components/explorer/BulkEditDialog";
import { ExportMenu } from "@/components/explorer/ExportMenu";

const PAGE = 50;

export default function DataExplorer() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const path = params.get("path") || "requests";
  const docId = params.get("doc");
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [pathInput, setPathInput] = useState(path);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const go = (p, d) => setParams(d ? { path: p, doc: d } : { path: p });
  const load = useCallback(async (after) => {
    setBusy(true);
    try {
      const res = await adapter.page(path, { pageSize: PAGE, after });
      setRows((prev) => (after ? [...prev, ...res.rows] : res.rows));
      setCursor(res.last); setDone(res.done);
    } catch (e) { toast.error(e.code === "permission-denied" ? `No read access to ${path}` : e.message); setRows([]); setDone(true); }
    finally { setBusy(false); }
  }, [path]);

  useEffect(() => { setPathInput(path); setQ(""); setSelected(new Set()); load(null); }, [path, load]);

  const ql = q.toLowerCase();
  const shown = ql ? rows.filter((r) => r.id.toLowerCase().includes(ql) || JSON.stringify(toPlain(r)).toLowerCase().includes(ql)) : rows;
  const segs = path.split("/").length;
  const validPath = (p) => p && p.split("/").filter(Boolean).length % 2 === 1;
  const admin = canSuperEdit(user);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (on) => setSelected((s) => { const n = new Set(s); shown.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });
  const selDocs = rows.filter((r) => selected.has(r.id));

  return (
    <div data-testid="data-explorer-page">
      <PageHeader title="Data Explorer" subtitle="Every Firestore collection in brugo-core-97965 — every field, live. Admins can edit, add, delete; every write is audited to activity_logs." />
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        <Card className="p-3 lg:max-h-[calc(100vh-200px)] overflow-y-auto"><CollectionList active={segs === 1 ? path : null} onPick={(c) => go(c)} /></Card>
        <Card className="min-w-0">
          <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
            <form className="flex gap-2 flex-1 min-w-[240px]" onSubmit={(e) => { e.preventDefault(); validPath(pathInput.trim()) ? go(pathInput.trim().replace(/^\/|\/$/g, "")) : toast.error("Path must point to a collection (odd number of segments)"); }}>
              <Input value={pathInput} onChange={(e) => setPathInput(e.target.value)} className="font-mono text-sm h-9" placeholder="collection or parent/id/subcollection" data-testid="explorer-path-input" />
              <Button type="submit" variant="outline" className="h-9" data-testid="explorer-path-go">Open</Button>
            </form>
            <div className="relative w-56">
              <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search loaded docs…" className="pl-8 h-9 text-sm" data-testid="explorer-search" />
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => load(null)} data-testid="explorer-refresh"><Icons.RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /></Button>
            <ExportMenu path={path} shown={shown} />
            {admin && <Button className="h-9 gap-1" onClick={() => setCreating(true)} data-testid="explorer-new-doc"><Icons.Plus className="h-4 w-4" /> New document</Button>}
          </div>
          {admin && selected.size > 0 && (
            <div className="px-4 py-2 border-b border-border bg-primary/5 flex items-center gap-3" data-testid="bulk-bar">
              <span className="text-xs font-medium" data-testid="bulk-selected-count">{selected.size} selected</span>
              <Button size="sm" className="h-7 gap-1" onClick={() => setBulkOpen(true)} data-testid="bulk-edit-btn"><Icons.PencilLine className="h-3.5 w-3.5" /> Bulk edit field</Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</Button>
            </div>
          )}
          <p className="px-4 pt-3 text-[11px] font-mono text-muted-foreground" data-testid="explorer-loaded-count">{path} · {shown.length} shown · {rows.length} loaded{done ? "" : "+"}</p>
          <DocTable rows={shown} onOpen={(id) => go(path, id)} selected={selected} onToggle={admin ? toggle : null} onToggleAll={toggleAll} />
          {!done && <div className="p-3 border-t border-border text-center"><Button variant="outline" size="sm" disabled={busy} onClick={() => load(cursor)} data-testid="explorer-load-more">Load {PAGE} more</Button></div>}
        </Card>
      </div>

      <Sheet open={!!docId} onOpenChange={(o) => !o && go(path)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="explorer-doc-drawer">
          {docId && (
            <>
              <SheetHeader className="text-left mb-4">
                <SheetTitle className="font-heading break-all">{docId}</SheetTitle>
                <SheetDescription className="font-mono break-all">{path}/{docId}</SheetDescription>
              </SheetHeader>
              <DocumentPanel path={path} id={docId} onDeleted={() => { setRows((r) => r.filter((x) => x.id !== docId)); go(path); }} />
            </>
          )}
        </SheetContent>
      </Sheet>
      <NewDocDialog path={path} open={creating} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); load(null); go(path, id); }} />
      {bulkOpen && <BulkEditDialog path={path} docs={selDocs} open onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); setSelected(new Set()); load(null); }} />}
    </div>
  );
}
